import {
  ReviewThreadStatus,
  type ReviewThreadListItemModel,
} from "@/api/cle-api";
import Button from "@/components/bricks/button/Button";
import AdminIcon from "@/components/icons/AdminIcon";
import CalendarIcon from "@/components/icons/CalendarIcon";
import CheckIcon from "@/components/icons/CheckIcon";
import ListIcon from "@/components/icons/ListIcon";
import PersonIcon from "@/components/icons/PersonIcon";
import { StatusBadge } from "@/components/pages/person/id/Efteruddannelse/StatusBadge";
import { formatDateTime } from "@/components/pages/person/id/Efteruddannelse/utils/formatters";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import Spinner from "@/components/spinner/Spinner";
import { UserQueryContext } from "@/context/UserContext";
import { useAdminMe, useMarkReviewThreadResolved } from "@/hooks/cle-hooks";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useContext, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useToaster } from "rsuite";

import { DisplayNameAvatar } from "./DisplayNameAvatar";
import { ReviewThreadAssignPopover } from "./ReviewThreadAssignPopover";
import { formatReviewSubjectType } from "./utils/reviewSubjectType";
import {
  formatReviewThreadStatus,
  getReviewThreadStatusVariant,
} from "./utils/reviewThreadStatus";

interface ReviewThreadTopPanelProps {
  threadId?: string;
  thread?: ReviewThreadListItemModel;
  ownerUserId?: string;
  isLoading?: boolean;
  error?: Error | null;
}

interface MetaFieldProps {
  label: string;
  icon: ReactNode;
  children: ReactNode;
}

const MetaField = ({ label, icon, children }: MetaFieldProps) => (
  <div className="c-review-thread-top-panel__field">
    <div className="c-review-thread-top-panel__label">
      <span
        className="c-review-thread-top-panel__label-icon"
        aria-hidden="true"
      >
        {icon}
      </span>
      {label}
    </div>
    <div className="c-review-thread-top-panel__value">{children}</div>
  </div>
);

export const ReviewThreadTopPanel = ({
  threadId,
  thread,
  ownerUserId,
  isLoading,
  error,
}: ReviewThreadTopPanelProps) => {
  const { user, hasPermission } = useContext(UserQueryContext);
  const toaster = useToaster();
  const canWrite = hasPermission(user, "TilsynWrite");
  const { data: adminMe } = useAdminMe();
  const { mutate: markResolved, isPending: isResolving } =
    useMarkReviewThreadResolved();

  const isResolved = thread?.status === ReviewThreadStatus.RESOLVED;
  // Sammenlign på CLE-API'ets admin-id'er — ikke på user.id fra Aika-claims, som hører
  // til en anden database, og ikke på navnet, som ikke er unikt. En tildeling kan ligge
  // under flere id'er for samme person, jf. assignment_user_ids.
  const isAssignedToMe =
    !!thread?.assignedAdminUserId &&
    !!adminMe?.assignment_user_ids?.includes(thread.assignedAdminUserId);
  const assignedAdminLabel =
    thread?.assignedAdminDisplayName ??
    (thread?.assignedAdminUserId ? "Ukendt admin" : null);
  const personId = thread?.ownerUserId ?? ownerUserId;
  const personName = thread?.ownerDisplayName ?? "Navn på person";
  const subjectLabel = thread
    ? formatReviewSubjectType(thread.subjectType)
    : "Henvendelse";
  const statusClass = thread?.status
    ? `c-review-thread-top-panel--${thread.status}`
    : "";

  const handleMarkResolved = () => {
    if (!threadId) return;
    markResolved(threadId, {
      onError: (err) => showCleApiErrorToast(toaster, err),
    });
  };

  const showActions =
    canWrite && !!threadId && !!thread && !isResolved && !isLoading;

  return (
    <div
      className={`c-card c-review-thread-top-panel mb-3 ${statusClass}`.trim()}
    >
      <div className="c-review-thread-top-panel__inner">
        <div className="c-review-thread-top-panel__header">
          <div>
            <span className="c-review-thread-top-panel__eyebrow">
              Henvendelse
            </span>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <h2 className="h4 mb-0">{subjectLabel}</h2>
              {thread ? (
                <StatusBadge
                  status={formatReviewThreadStatus(thread.status)}
                  variant={getReviewThreadStatusVariant(thread.status)}
                  pill
                />
              ) : null}
            </div>
          </div>

          {showActions ? (
            <div className="c-review-thread-top-panel__actions">
              <ReviewThreadAssignPopover
                threadId={threadId!}
                isAssignedToMe={isAssignedToMe}
                disabled={isResolving}
              />
              <Button
                id="review-thread-mark-resolved"
                color="-action"
                type="button"
                disabled={isResolving || !isAssignedToMe}
                title={
                  isAssignedToMe
                    ? undefined
                    : "Henvendelsen skal være tildelt dig, før du kan markere den som løst."
                }
                onClick={handleMarkResolved}
              >
                {isResolving ? <Spinner size={14} /> : null}
                Markér som løst
              </Button>
              {isAssignedToMe ? null : (
                <p className="c-review-thread-top-panel__actions-hint">
                  Kun den tildelte bruger kan markere som løst.
                </p>
              )}
            </div>
          ) : null}
        </div>

        {thread && isResolved ? (
          <div className="c-review-thread-top-panel__resolved">
            <CheckIcon width={18} height={18} />
            <span>
              Løst af {thread.resolvedByAdminDisplayName ?? "ukendt admin"}
              {thread.resolvedAt
                ? ` · ${formatDateTime(thread.resolvedAt)}`
                : null}
            </span>
          </div>
        ) : null}

        {thread && !isLoading ? (
          <div className="c-review-thread-top-panel__meta">
            <MetaField
              label="Person"
              icon={<PersonIcon width={15} height={15} />}
            >
              {personId ? (
                <Link
                  to={`/person/${personId}`}
                  className="link-primary text-decoration-none"
                >
                  {personName}
                </Link>
              ) : (
                personName
              )}
            </MetaField>

            {isResolved ? (
              <MetaField
                label="Løst af"
                icon={<AdminIcon width={15} height={15} />}
              >
                {thread.resolvedByAdminDisplayName ? (
                  <DisplayNameAvatar
                    name={thread.resolvedByAdminDisplayName}
                    showName
                  />
                ) : (
                  "-"
                )}
              </MetaField>
            ) : (
              <MetaField
                label="Tildelt til"
                icon={<AdminIcon width={15} height={15} />}
              >
                {assignedAdminLabel ? (
                  <DisplayNameAvatar name={assignedAdminLabel} showName />
                ) : (
                  <span className="text-muted fw-semibold">Ikke tildelt</span>
                )}
              </MetaField>
            )}

            <MetaField
              label={"Seneste kommentar"}
              icon={<CalendarIcon width={15} height={15} />}
            >
              {formatDateTime(thread.lastCommentAt)}
            </MetaField>

            <MetaField
              label="Kommentarer"
              icon={<ListIcon width={15} height={15} />}
            >
              <span>{thread.commentCount}</span>
            </MetaField>
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="c-review-thread-top-panel__loading">
          <Spinner label="Henter henvendelse ..." />
        </div>
      ) : null}

      {error ? (
        <CleApiErrorText
          error={error}
          fallback="Kunne ikke hente henvendelsesoplysninger."
          className="c-review-thread-top-panel__error text-danger"
        />
      ) : null}
    </div>
  );
};
